import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Sparkles, Eye, EyeOff, Phone } from 'lucide-react';

/**
 * Account-claim page — landed on from the post-purchase email.
 *
 * Flow:
 *   1. User receives "Welcome to Learnable" email after a guest purchase
 *   2. Clicks the link → here, with ?token=<one-time-claim-token>
 *   3. Confirms name (prefilled from purchase) + sets a password
 *   4. POST /api/auth/claim-account → returns JWT, we auto-login + redirect
 */
export default function ClaimAccount() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!token) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <AlertCircle className="text-rose-500 mx-auto mb-3" size={32} />
        <h1 className="text-xl font-bold mb-2">Invalid link</h1>
        <p className="text-sm text-slate-600">
          This page expects a setup token in the URL. Check the link in your purchase confirmation email.
        </p>
        <Link to="/" className="inline-block mt-4 text-sm text-brand underline">Back to home</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8 || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) return setError('Password must be 8+ characters with uppercase, lowercase, and a number.');
    if (password !== confirm) return setError("Passwords don't match.");

    setBusy(true);
    try {
      const res = await fetch('/api/auth/claim-account', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'invalid_or_expired_token') {
          setError("This setup link has expired or is invalid. Contact support@learnableskill.com for a new one.");
        } else {
          setError(data.error || 'Setup failed. Try again.');
        }
        return;
      }

      localStorage.setItem('learnable_user', JSON.stringify(data.user));
      navigate('/dashboard');
    } catch (err) {
      setError('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <div className="w-12 h-12 mx-auto rounded-full bg-brand/10 text-brand flex items-center justify-center mb-3">
          <Sparkles size={22} />
        </div>
        <h1 className="text-2xl font-bold">Welcome to Learnable</h1>
        <p className="text-slate-600 text-sm mt-2">
          Set your password to access your course.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-white border border-slate-200 rounded-lg p-6">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
            Full name <span className="text-slate-400 normal-case">(optional — to update)</span>
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            placeholder="Leave blank to keep what you entered at checkout"
            maxLength={120}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
            Phone number <span className="text-slate-400 normal-case">(optional)</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            placeholder="(555) 123-4567"
            maxLength={30}
          />
          <span className="inline-block mt-1.5 text-xs text-slate-600 bg-slate-100 rounded-full px-3 py-1">
            For course access issues only — we won't text you marketing.
          </span>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
            Choose a password
          </label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              placeholder="8+ characters"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">
            Confirm password
          </label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              tabIndex={-1}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          disabled={busy}
          className="w-full bg-brand text-white rounded-lg py-2.5 font-semibold hover:bg-brand-dark disabled:opacity-50"
        >
          {busy ? 'Setting up…' : 'Finish setup & log in'}
        </button>
      </form>
    </div>
  );
}
